import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Section, SectionHeader, RowGroup, ToggleRow, RadioRow, SaveButton } from "./_helpers";
import { MessageSquare, Clock, AlertCircle, Filter, Users, Heart } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AGE_OPTS = [18, 21, 25, 30, 40];
const NEW_ACC_OPTS = [0, 1, 7, 14, 30];

export default function MeldingerPage() {
  const { t } = useTranslation();
  const { user, linkedCouple } = useAuth();
  const [data, setData] = useState(null);
  const [dirtyWelcome, setDirtyWelcome] = useState(null);
  const [dirtyWords, setDirtyWords] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shareDm, setShareDm] = useState(false);
  const canShowCoupleShare = user && user !== false && user.linked_couple_id && user.role !== "couple";

  const WHO_OPTS = [
    { v: "everyone_logged_in", label: t("meldinger_settings.who_everyone") },
    { v: "followers", label: t("meldinger_settings.who_followers") },
    { v: "friends", label: t("meldinger_settings.who_friends") },
    { v: "vip_only", label: t("meldinger_settings.who_vip") },
    { v: "nobody", label: t("meldinger_settings.who_nobody") },
  ];

  const ARCHIVE_OPTS = [
    { v: null, label: t("meldinger_settings.archive_never") },
    { v: 30, label: t("meldinger_settings.archive_days", { count: 30 }) },
    { v: 60, label: t("meldinger_settings.archive_days", { count: 60 }) },
    { v: 90, label: t("meldinger_settings.archive_days", { count: 90 }) },
  ];

  useEffect(() => {
    api.get("/users/me/dm-prefs").then(r => setData(r.data)).catch(() => {});
    if (user && user !== false) setShareDm(!!user.dm_shared_with_couple);
  }, [user]);

  const updateDmShare = async (v) => {
    setShareDm(v);
    try {
      await api.put("/me/couple/dm-share", { enabled: v });
      toast.success(v ? t("meldinger_settings.couple_dm_on_toast") : t("meldinger_settings.couple_dm_off_toast"));
    } catch (e) {
      toast.error(formatApiError(e));
      setShareDm(!v);
    }
  };

  useEffect(() => { api.get("/users/me/dm-prefs").then(r=>setData(r.data)).catch(()=>{}); }, []);

  const update = async (patch) => {
    const next = { ...data, ...patch };
    setData(next);
    try { await api.patch("/users/me/dm-prefs", patch); }
    catch (e) {
      toast.error(formatApiError(e));
      api.get("/users/me/dm-prefs").then(r=>setData(r.data));
    }
  };

  const saveWelcome = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/dm-prefs", { custom_welcome_message: dirtyWelcome });
      setData({ ...data, custom_welcome_message: dirtyWelcome });
      setDirtyWelcome(null);
      toast.success(t("meldinger_settings.saved_toast"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveWords = async () => {
    setSaving(true);
    try {
      const words = (dirtyWords || "").split(",").map(w=>w.trim()).filter(Boolean);
      await api.patch("/users/me/dm-prefs", { blocklist_words: words });
      setData({ ...data, blocklist_words: words });
      setDirtyWords(null);
      toast.success(t("meldinger_settings.saved_toast"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="font-meta text-[var(--cream-soft)]">{t("meldinger_settings.loading")}</div>;

  return (
    <>
      {canShowCoupleShare && linkedCouple && (
        <Section testId="couple-dm-share-section">
          <SectionHeader icon={Heart} title={t("meldinger_settings.couple_share_title")}
            desc={t("meldinger_settings.couple_share_desc", { name: linkedCouple.name })} />
          <RowGroup>
            <ToggleRow
              testId="couple-dm-share-toggle"
              label={t("meldinger_settings.couple_share_toggle")}
              sub={t("meldinger_settings.couple_share_sub", { user: user.username })}
              checked={shareDm}
              onChange={updateDmShare}
            />
          </RowGroup>
          <p className="font-meta text-[10px] text-[var(--cream-faint)] mt-3 max-w-md leading-relaxed">
            {t("meldinger_settings.couple_share_note")}
          </p>
        </Section>
      )}

      <Section testId="who-can-dm-section">
        <SectionHeader icon={MessageSquare} title={t("meldinger_settings.who_can_dm")} />
        <RowGroup>
          {WHO_OPTS.map((o) => (
            <RadioRow key={o.v} name="who_can_dm" value={o.v} current={data.who_can_dm}
              label={o.label} testId={`dm-who-${o.v}`} onChange={(v)=>update({who_can_dm:v})} />
          ))}
        </RowGroup>
        <div className="mt-4">
          <RowGroup>
            <ToggleRow testId="require-mutual-follow" label={t("meldinger_settings.require_mutual")}
              sub={t("meldinger_settings.require_mutual_sub")}
              checked={data.require_mutual_follow} onChange={(v)=>update({require_mutual_follow:v})} />
          </RowGroup>
        </div>
      </Section>

      <Section testId="first-time-warning-section">
        <SectionHeader icon={AlertCircle} title={t("meldinger_settings.welcome_title")} desc={t("meldinger_settings.welcome_desc")} />
        <RowGroup>
          <ToggleRow testId="warn-first-time" label={t("meldinger_settings.welcome_toggle")}
            sub={t("meldinger_settings.welcome_sub")}
            checked={data.warn_first_time_senders} onChange={(v)=>update({warn_first_time_senders:v})} />
        </RowGroup>
        <div className="mt-6">
          <label className="block">
            <span className="font-manrope text-sm text-[var(--cream)] block mb-2">
              {t("meldinger_settings.custom_welcome")}
            </span>
            <textarea
              value={dirtyWelcome ?? data.custom_welcome_message ?? ""}
              onChange={(e)=>setDirtyWelcome(e.target.value)}
              maxLength={100}
              rows={3}
              placeholder={t("meldinger_settings.custom_welcome_placeholder")}
              className="input-editorial w-full max-w-md"
              data-testid="welcome-message"
            />
            <div className="font-meta text-[var(--cream-faint)] mt-1">{(dirtyWelcome ?? data.custom_welcome_message ?? "").length}/100 (VIP: 280)</div>
          </label>
          {dirtyWelcome !== null && (
            <SaveButton onClick={saveWelcome} saving={saving} testId="save-welcome">{t("meldinger_settings.save_welcome")}</SaveButton>
          )}
        </div>
      </Section>

      <Section testId="dm-filters-section">
        <SectionHeader icon={Filter} title={t("meldinger_settings.filters_title")} />
        <RowGroup>
          <ToggleRow testId="block-no-avatar" label={t("meldinger_settings.block_no_avatar")}
            checked={data.block_no_avatar} onChange={(v)=>update({block_no_avatar:v})} />
          <ToggleRow testId="block-media-first" label={t("meldinger_settings.block_media_first")}
            sub={t("meldinger_settings.block_media_first_sub")}
            checked={data.block_media_first_dm} onChange={(v)=>update({block_media_first_dm:v})} />
        </RowGroup>
        <div className="mt-6 space-y-4">
          <div>
            <label className="font-manrope text-sm text-[var(--cream)] block mb-2">{t("meldinger_settings.block_younger_label")}</label>
            <div className="flex gap-2 flex-wrap" data-testid="block-younger-options">
              {NEW_ACC_OPTS.map((d) => (
                <button key={d} onClick={()=>update({block_account_younger_than_days:d})}
                  className={`px-3 py-1 text-sm border ${data.block_account_younger_than_days === d ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)]"} hover:border-[var(--magenta)]/60`}>
                  {d === 0 ? t("meldinger_settings.off") : `${d} ${t("meldinger_settings.days_short")}`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-manrope text-sm text-[var(--cream)] block mb-2">
              {t("meldinger_settings.min_sender_age")} <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-[var(--gold)]/20 text-[var(--gold)] border border-[var(--gold)]/30">{t("meldinger_settings.vip_above_18")}</span>
            </label>
            <div className="flex gap-2 flex-wrap" data-testid="min-age-options">
              {AGE_OPTS.map((a) => (
                <button key={a} onClick={()=>update({min_sender_age:a})}
                  className={`px-3 py-1 text-sm border ${data.min_sender_age === a ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)]"} hover:border-[var(--magenta)]/60`}>
                  {a}+
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section testId="word-filter-section">
        <SectionHeader title={t("meldinger_settings.word_filter_title")} desc={t("meldinger_settings.word_filter_desc")} />
        <textarea
          value={dirtyWords ?? (data.blocklist_words || []).join(", ")}
          onChange={(e)=>setDirtyWords(e.target.value)}
          rows={3}
          placeholder={t("meldinger_settings.word_filter_placeholder")}
          className="input-editorial w-full max-w-md"
          data-testid="word-filter-input"
        />
        <div className="font-meta text-[var(--cream-faint)] mt-1">{t("meldinger_settings.word_filter_max")}</div>
        {dirtyWords !== null && (
          <SaveButton onClick={saveWords} saving={saving} testId="save-words">{t("meldinger_settings.save_filter")}</SaveButton>
        )}
      </Section>

      <Section testId="vip-hide-section">
        <SectionHeader title={t("meldinger_settings.vip_hide_title")} />
        <RowGroup>
          <ToggleRow testId="hide-typing" vip label={t("meldinger_settings.hide_typing")}
            sub={t("meldinger_settings.hide_typing_sub")}
            checked={data.hide_typing} onChange={(v)=>update({hide_typing:v})} />
          <ToggleRow testId="hide-read-receipts" vip label={t("meldinger_settings.hide_read")}
            sub={t("meldinger_settings.hide_read_sub")}
            checked={data.hide_read_receipts} onChange={(v)=>update({hide_read_receipts:v})} />
        </RowGroup>
      </Section>

      <Section testId="quiet-hours-section">
        <SectionHeader icon={Clock} title={t("meldinger_settings.quiet_title")} desc={t("meldinger_settings.quiet_desc")} />
        <RowGroup>
          <ToggleRow testId="quiet-enabled" label={t("meldinger_settings.quiet_enable")}
            checked={data.quiet_hours?.enabled} onChange={(v)=>update({quiet_hours: { ...data.quiet_hours, enabled:v }})} />
        </RowGroup>
        {data.quiet_hours?.enabled && (
          <div className="mt-4 flex items-center gap-4">
            <label className="flex items-center gap-2">
              <span className="font-manrope text-sm text-[var(--cream-soft)]">{t("meldinger_settings.from")}</span>
              <input type="time" value={data.quiet_hours?.start || "22:00"}
                onChange={(e)=>update({ quiet_hours: { ...data.quiet_hours, start: e.target.value }})}
                className="input-editorial" data-testid="quiet-start" />
            </label>
            <label className="flex items-center gap-2">
              <span className="font-manrope text-sm text-[var(--cream-soft)]">{t("meldinger_settings.to")}</span>
              <input type="time" value={data.quiet_hours?.end || "07:00"}
                onChange={(e)=>update({ quiet_hours: { ...data.quiet_hours, end: e.target.value }})}
                className="input-editorial" data-testid="quiet-end" />
            </label>
          </div>
        )}
      </Section>

      <Section testId="auto-archive-section">
        <SectionHeader title={t("meldinger_settings.archive_title")} desc={t("meldinger_settings.archive_desc")} />
        <div className="flex gap-2 flex-wrap" data-testid="archive-options">
          {ARCHIVE_OPTS.map((o) => (
            <button key={String(o.v)} onClick={()=>update({auto_archive_after_days: o.v})}
              className={`px-3 py-1 text-sm border ${data.auto_archive_after_days === o.v ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)]"} hover:border-[var(--magenta)]/60`}>
              {o.label}
              <span className="ml-2 inline-block px-1 py-0 text-[9px] uppercase bg-[var(--gold)]/20 text-[var(--gold)]">VIP</span>
            </button>
          ))}
        </div>
      </Section>

      <Section testId="group-dm-section">
        <SectionHeader icon={Users} title={t("meldinger_settings.group_dm_title")}
          desc={t("meldinger_settings.group_dm_desc")} />
        <RowGroup>
          <ToggleRow testId="allow-group-dm" label={t("meldinger_settings.group_dm_toggle")}
            sub={t("meldinger_settings.group_dm_sub")}
            checked={data.allow_group_dm} onChange={(v)=>update({allow_group_dm:v})} />
        </RowGroup>
      </Section>
    </>
  );
}
